import { formatCZK, formatCZKCompact } from "@/lib/format";
import { useIsMobile } from "@/components/shared/PagePrimitives";
import { LineChart, Legend, TipRow, MONTHS_SHORT, monthName, cap } from "@/components/economics/overview/charts";
import "@/components/economics/overview/ecoOverview.css";

// Ekonomika zakázky v čase (290) — stejný graf jako Přehled Ekonomiky: vyfakturováno
// a náklady sečtené od prvního dokladu, čárkovaně cena díla a rozpočet. Body po týdnech,
// zakázka delší než ~7 měsíců po měsících.
const C = { inv: "var(--ov-in)", cost: "var(--ov-out)", red: "var(--ov-red)", budget: "var(--ov-budget)" };
const DAY = 864e5;
const iso = (t) => new Date(t).toISOString().slice(0, 10);
const money = (v) => formatCZKCompact(Math.round(v || 0));
const signed = (v) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${formatCZKCompact(Math.abs(Math.round(v || 0)))}`;
const dm = (day) => `${Number(day.slice(8, 10))}. ${Number(day.slice(5, 7))}.`;

// Body grafu z kumulativních řad serveru ([{ date, cumulative }]): den před prvním dokladem
// (nula), konce týdnů / měsíců a poslední den (dnes nebo pozdější doklad).
export function projectTimeline(costs = [], invoiced = [], today = iso(Date.now())) {
  const days = [...costs, ...invoiced].map((p) => p.date).filter(Boolean).sort();
  if (!days.length) return [];
  const first = days[0];
  const end = days[days.length - 1] > today ? days[days.length - 1] : today;
  const t0 = Date.parse(first), t1 = Date.parse(end);
  const monthly = (t1 - t0) / DAY > 210;
  const ends = [iso(t0 - DAY)];
  if (monthly) {
    for (let y = Number(first.slice(0, 4)), m = Number(first.slice(5, 7)); ; m++) {
      if (m > 12) { m = 1; y++; }
      const last = iso(Date.UTC(y, m, 0));   // poslední den měsíce
      if (last >= end) break;
      ends.push(last);
    }
  } else {
    for (let t = t0 + 6 * DAY; t < t1; t += 7 * DAY) ends.push(iso(t));
  }
  ends.push(end);
  const byDate = (s) => s.filter((p) => p.date).slice().sort((a, b) => a.date.localeCompare(b.date));
  const cs = byDate(costs), is = byDate(invoiced);
  const at = (s, day) => { let v = 0; for (const p of s) { if (p.date > day) break; v = p.cumulative || 0; } return v; };
  return ends.map((day) => ({ day, monthly, cost: at(cs, day), inv: at(is, day) }));
}

export function ProjectTimeChart({ costSeries, invoicedSeries, invoiced, costs, contract, budget, vatWord }) {
  const mobile = useIsMobile();
  const pts = projectTimeline(costSeries, invoicedSeries);
  const n = pts.length;
  // popisky osy: na PC nejvýš ~7 a poslední vždy; na telefonu ~4 na sudých bodech
  // (úzký graf sám skrývá liché popisky, když je bodů víc než 9)
  const step = mobile ? 2 * Math.max(1, Math.ceil(n / 8)) : Math.max(1, Math.ceil(n / 7));
  const shown = (i) => (mobile ? i % step === 0 : (n - 1 - i) % step === 0);
  const labels = pts.map((p, i) => (!shown(i) ? "" : p.monthly ? MONTHS_SHORT[Number(p.day.slice(5, 7)) - 1] : dm(p.day)));
  const pct = contract > 0 ? Math.round((invoiced / contract) * 100) : null;
  const sub = [pct != null ? `${pct} % ceny díla` : null, `náklady ${money(costs)}`, vatWord].filter(Boolean).join(" · ");
  const refs = [
    ...(contract > 0 ? [{ value: contract, name: "Cena díla", label: `Cena díla ${money(contract)}`, color: C.inv, text: "var(--ov-in-text)" }] : []),
    ...(budget > 0 ? [{ value: budget, name: "Rozpočet", label: `Rozpočet ${money(budget)}`, color: C.budget, text: "hsl(var(--muted-foreground))" }] : []),
  ].sort((a, b) => b.value - a.value);
  const tip = (i) => {
    const p = pts[i];
    const d = p.inv - p.cost;
    return (
      <>
        <div className="tt">{p.monthly ? cap(monthName(p.day.slice(0, 7))) : `do ${dm(p.day)} ${p.day.slice(0, 4)}`}</div>
        <TipRow color={C.inv} value={money(p.inv)} label="vyfakturováno" />
        <TipRow color={C.cost} value={money(p.cost)} label="náklady" />
        <TipRow color={d < 0 ? C.red : C.inv} value={signed(d)} label="rozdíl" />
      </>
    );
  };
  return (
    <div className="eco-ov min-w-0">
      <section className="bg-card border border-border rounded-[20px] p-4 md:p-6 min-w-0" data-testid="costs-invoicing-chart">
        <div className="text-[15px] font-medium text-muted-foreground">Vyfakturováno a náklady</div>
        <div className="ov-num ov-in-text font-heading font-extrabold tracking-tight tabular-nums whitespace-nowrap text-[28px] md:text-[36px] leading-tight mt-1"
          title={formatCZK(invoiced)}>{money(invoiced)}</div>
        <div className="text-sm text-muted-foreground mt-0.5">{sub}</div>
        {n > 1 ? (
          <>
            <div className="mt-6">
              <LineChart height={mobile ? 200 : 240} compact={mobile} labels={labels} refs={refs} tip={tip}
                series={[{ values: pts.map((p) => p.inv), color: C.inv }, { values: pts.map((p) => p.cost), color: C.cost }]} />
            </div>
            <div className="mt-3">
              <Legend items={[{ label: "Vyfakturováno", color: C.inv, line: true }, { label: "Náklady", color: C.cost, line: true },
                ...refs.map((r) => ({ label: r.name, color: r.color, dash: true }))]} />
            </div>
          </>
        ) : <p className="text-muted-foreground text-sm mt-4">Zatím žádné doklady.</p>}
      </section>
    </div>
  );
}
